import React from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, H1, Icon, Link, Separator, Text } from '@betterment/samba-react';
import { CardLoader, ErrorBoundary } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { AccountOverviewPortfolioDocument } from './graphql/query.portfolio.generated';

import urlBuilder from '../../../Shared/url-builder';
import FormButton from '../../FormButton';

export const PortfolioStrategyCardView: React.FC = () => {
  const accountId = useRequiredParam('accountId');
  const { data: portfolioData } = useGraphQLQuery(AccountOverviewPortfolioDocument, {
    variables: { id: accountId },
  });

  const envelopeAccount = portfolioData?.envelopeAccount;

  if (!portfolioData || !envelopeAccount) {
    return <CardLoader />;
  }

  if (envelopeAccount.__typename !== 'ManagedAccount') {
    return null;
  }

  const { portfolio } = envelopeAccount;
  const { drift, rebalance } = portfolio;

  const { isAboveThreshold } = drift;
  const driftCurrent = drift.current.toFixed(1);
  const driftThreshold = drift.threshold.toFixed(1);

  const rebalancingUrl = urlBuilder.getGoalRebalanceSettingsEditPage(envelopeAccount.legacyId);

  return (
    <Card
      css={{
        color: 'text',
        textStyle: 'text100',
        width: '100%',
        p: 'space.300',
      }}
    >
      <Card.Header css={{ display: 'flex', justifyContent: 'space-between' }}>
        <H1 variant="title200" css={{ pb: 'space.200' }}>
          Portfolio strategy
        </H1>
      </Card.Header>
      <Card.Content css={{ display: 'flex', flexDirection: 'column' }}>
        <Separator />
        <Box
          css={{
            py: 'space.200',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box css={{ display: 'flex', flexDirection: 'column' }}>
            <Text variant="text50">Target portfolio</Text>
            <Text variant="text100" data-testid="strategy-name">
              {portfolio.strategyName}
            </Text>
          </Box>
          {envelopeAccount.legacyId != null && (
            <FormButton
              method="post"
              action={urlBuilder.getEditGoalPortfolioPage(envelopeAccount.legacyId, window.location.pathname)}
              variant="textPrimary"
              data-testId="edit-target-portfolio-link"
            >
              Edit
            </FormButton>
          )}
        </Box>
        <Separator />
        <Box
          css={{
            py: 'space.200',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box css={{ display: 'flex', flexDirection: 'column' }}>
            <Text variant="text50">Target allocation</Text>
            <Text variant="text100" data-testid="target-allocation-description">
              {portfolio.targetAllocationDescription}
            </Text>
          </Box>
        </Box>
        <Separator />
        <Box
          css={{
            py: 'space.200',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box css={{ display: 'flex', flexDirection: 'column' }}>
            <Text variant="text50">Rebalancing</Text>
            <Text variant="text100" data-testid="rebalancing-enabled">
              {rebalance?.isEnabled ? 'On' : 'Off'}
            </Text>
          </Box>
          <Link
            href={rebalancingUrl}
            variant="textPrimary"
            data-testid="change-rebalancing-link"
            style={{ textAlign: 'right' }}
          >
            Change
          </Link>
        </Box>
        <Separator />
        <Box
          css={{
            pt: 'space.200',
            display: 'flex',
            flexDirection: 'column',
            gap: 'space.050',
          }}
        >
          <Text variant="text50">Drift</Text>
          <Text variant="text100" color={isAboveThreshold ? 'text.warning' : undefined} data-testid="drift-text">
            {isAboveThreshold ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'left' }}>
                <Icon color="icon.warning" name="WarningFilled" size="xsmall" data-testid="warning-icon" />
                High ({driftCurrent}%)
              </span>
            ) : (
              `${driftCurrent}%`
            )}
          </Text>
          <Text variant="text50" color="text.weak">
            Threshold: {driftThreshold}%
          </Text>
        </Box>
      </Card.Content>
    </Card>
  );
};

export const PortfolioStrategyCard: React.FC = () => (
  <ErrorBoundary fallbackType="none">
    <PortfolioStrategyCardView />
  </ErrorBoundary>
);
